import Spinners from "components/Common/Spinner"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { useFormik } from "formik"
import moment from "moment"
import React from "react"
import toast from "react-hot-toast"
import { useSelector } from "react-redux"
import {
  Col,
  Container,
  Form,
  FormFeedback,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import {
  useHelpMarkClosedMutation,
  useSubmitReplyMutation,
} from "store/api/user/help.api"
import * as Yup from "yup"

function HelpModelView({ open, setOpen, data, onSuccess, onClose }) {
  const toggle = () => setOpen(!open)
  const { userType } = useSelector((state) => state.user)
  const [submitReply, { isLoading }] = useSubmitReplyMutation()
  const [closeTicket, { isLoading: isClosing }] = useHelpMarkClosedMutation()

  const getName = (item) => {
    const map = {
      ADMIN: {
        ADMIN: "You",
        INVESTOR: "Investor",
        WEALTH_PARTNER: "Wealth Partner",
      },
      INVESTOR: { INVESTOR: "You", ADMIN: "Admin" },
      WEALTH_PARTNER: { WEALTH_PARTNER: "You", ADMIN: "Admin" },
    }
    return map[userType]?.[item.replied_by] || ""
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { message: "" },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      handleFormSubmit(values)
    },
  })

  const handleFormSubmit = async (values) => {
    try {
      const { success, message } = await submitReply({
        id: data.id,
        ...values,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "toastMessage" })
        onSuccess(data.id)
        validation.setFieldValue("message", "")
        validation.resetForm()
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const handleCloseTicket = async () => {
    try {
      const { success, message } = await closeTicket({
        id: data.id,
        status: data.status,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "toastMessage" })
        onClose(data.id)
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const getStatus = () => {
    let status = "Active"
    let className = "Active"
    const key = data.status

    switch (key) {
      case "OPEN":
        status = "Open"
        className = "under_review"
        break

      case "REOPEN":
        status = "Re-open"
        className = "query_raised"
        break

      default:
        status = "Closed"
        className = "approved"
        break
    }

    return (
      <div className={`badge ${className}`}>
        <span></span> {status}
      </div>
    )
  }

  return (
    <div>
      <Modal size="lg" isOpen={open} toggle={toggle} centered>
        <ModalHeader
          toggle={() => {
            setOpen((prev) => !prev)
          }}
        >
          Help Request
        </ModalHeader>
        <ModalBody>
          <Container>
            <div className="form-horizontal">
              <Row className="mb-2">
                <Col xl={6} className="mb-2">
                  <Label>Ticket ID</Label>
                  <p>{data.unique_code}</p>
                </Col>
                <Col xl={6} className="mb-2">
                  <Label>Date</Label>
                  <p>{data.formatted_created_at}</p>
                </Col>
                {userType === "ADMIN" && (
                  <Col xl={6} className="mb-2">
                    <Label className="form-label">
                      {data?.investor_id ? "Investor" : "Wealth Partner"}
                    </Label>
                    {(() => {
                      const ticketUser = data?.investor_id
                        ? data?.investor
                        : data?.wealthPartner
                      return (
                        <DefaultAvatar
                          className="justify-content-start"
                          avatar_url={ticketUser?.avatar_url}
                          email={ticketUser?.email}
                          name={ticketUser?.name}
                        />
                      )
                    })()}
                  </Col>
                )}

                <Col xl={6} className="mb-2">
                  <Label className="form-label">Status</Label>
                  <div
                    style={{
                      display: "flex",
                      gap: "20px",
                      alignItems: "center",
                    }}
                  >
                    <div className="code-status">{getStatus()}</div>
                    {userType === "ADMIN" &&
                      ["REOPEN", "OPEN"].includes(data.status) && (
                        <button
                          type="button"
                          className="approve btn btn-secondary"
                          onClick={() => handleCloseTicket()}
                        >
                          {isClosing ? <Spinners /> : "Close"}
                        </button>
                      )}
                    {data.status === "CLOSED" && (
                      <button
                        type="button"
                        className="approve btn btn-secondary"
                        onClick={() => handleCloseTicket()}
                      >
                        {isClosing ? <Spinners /> : "Re-Open"}
                      </button>
                    )}
                  </div>
                </Col>
                <hr />

                <Col xl={12} className="mb-2">
                  <Label className="form-label">
                    {getName({ replied_by: data.created_by })}
                  </Label>
                  <div style={{ paddingLeft: "10px" }}>
                    <h6>{data?.message}</h6>
                    <p>
                      <small>
                        {moment(data.created_at).format("D MMM YYYY, hh:mm A")}
                      </small>
                    </p>
                  </div>
                </Col>
                <hr />
                {data.helpThreads.map((item, index) => (
                  <Col xl={12} className="mb-2" key={`threads_${index}`}>
                    <Label className="form-label">{getName(item)} : </Label>
                    <div style={{ paddingLeft: "10px" }}>
                      <h6>{item?.message}</h6>
                      <p>
                        <small>
                          {moment(item.created_at).format(
                            "D MMM YYYY, hh:mm A"
                          )}
                        </small>
                      </p>
                    </div>
                    {data.helpThreads.length - 1 > index && <hr />}
                  </Col>
                ))}
                {["REOPEN", "OPEN"].includes(data.status) && (
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    <div
                      className="form-group mt-3"
                      style={{ position: "relative", zIndex: 2 }}
                    >
                      <label htmlFor="rejection-message">Message</label>
                      <textarea
                        name="message"
                        id="rejection-message"
                        type="text"
                        className="form-control"
                        rows="3"
                        value={validation.values.message}
                        onChange={validation.handleChange}
                        placeholder="Enter Message"
                      />
                      {validation.errors.message && (
                        <FormFeedback type="invalid">
                          {validation.errors.message}
                        </FormFeedback>
                      )}
                    </div>
                    <div className="mt-3 pt-3 button-wrapper position-relative z-2">
                      <button
                        type="submit"
                        className="approve btn btn-secondary"
                      >
                        {isLoading ? <Spinners /> : "Submit Reply"}
                      </button>
                    </div>
                  </Form>
                )}
              </Row>
            </div>
          </Container>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default HelpModelView
